import React, { useEffect, useState } from 'react';
import { CalendarIcon, UserIcon, TrendingUpIcon, GlobeIcon, ShieldCheckIcon, SparklesIcon, MapPinIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
type Activity = {
  date: string;
  deliveryLead: string;
  session: string;
  focus: string;
  imageUrl?: string;
  imageType: 'photo' | 'logo' | 'placeholder';
  initials?: string;
  isMandatory?: boolean;
  sessionFormat?: string;
};
type ItineraryEvent = {
  date: string;
  time?: string;
  title: string;
  location?: string;
  description: string;
  imageUrl: string;
  accentColor: string;
};
type Investor = {
  name: string;
  firm: string;
  bio: string;
  imageUrl: string;
  accentColor: string;
};
type Outcome = {
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
};
const activities: Activity[] = [{
  date: 'Week of January 12',
  deliveryLead: 'Bryan Duarte',
  session: 'Investor Readiness (1:1 Sessions)',
  focus: 'Personalized fundraising strategy, investor positioning, pitch refinement',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/2SVPCaSPdcuMiDCUTYJVNi/board-bryan-duarte.webp',
  imageType: 'photo',
  isMandatory: true,
  sessionFormat: '1:1 Sessions'
}, {
  date: 'January 19',
  deliveryLead: 'Chido Samantha Mbavarira',
  session: 'Sales On Steroids',
  focus: 'Partnerships, enterprise sales, corporate procurement pathways',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/qnuDzkr8qndZuV37wycVYH/Chido.png',
  imageType: 'photo',
  sessionFormat: 'Live Group Session'
}, {
  date: 'January 20',
  deliveryLead: 'Canada–Qatar Trade Commissioner Office & Qatar Development Bank',
  session: 'Pre-Industry Briefing Event',
  focus: 'Market entry insights, public–private partnerships, regional opportunity mapping',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/3wzMxZ2fwjYAH3RTybuSEK/QDB_LOGO.png',
  imageType: 'logo',
  isMandatory: true
}, {
  date: 'January 23',
  deliveryLead: 'Nada Shaheen',
  session: 'Investor Readiness & Corporate Strategy in MENA',
  focus: 'Capital strategy, regional investor expectations, scaling in the Middle East',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/4g3QxcdBSA9yMMybLmF9y7/Nada.jpg',
  imageType: 'photo',
  sessionFormat: 'Live Group Session'
}, {
  date: 'January 26',
  deliveryLead: 'Layla Freig',
  session: 'Financial Office Hours',
  focus: 'Financial modeling, metrics alignment, investor-grade financial storytelling',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gUaEiNEnE8jd2vPJJ8c2VY/Layla_Freig.jpg',
  imageType: 'photo'
}, {
  date: 'January 29',
  deliveryLead: 'Tehmina Chaudhry',
  session: 'Mandatory Cultural Awareness Session',
  focus: 'Cultural intelligence, business etiquette, representation in Qatar and MENA markets',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/wA4EsNWLPD7oF4xVmQc1cL/Tehmina.jpg',
  imageType: 'photo',
  isMandatory: true
}];
const itineraryEvents: ItineraryEvent[] = [{
  date: 'Feb 1',
  time: 'Morning',
  title: 'Women in Tech Breakfast',
  location: 'Presented by A Typical Ventures',
  description: 'Exclusive networking breakfast connecting women founders, investors, and tech leaders in the MENA ecosystem',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/cmoWkb8vsMGEphPkiV1HjV/Women_In_Tech_Breakfast.png',
  accentColor: 'from-amber-500 to-yellow-600'
}, {
  date: 'Feb 1',
  time: 'Evening',
  title: 'Dinner at the Museum',
  location: 'National Museum of Qatar',
  description: 'Orientation + expectations setting',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/dQHESQ8jXs2k4Euq3vKp4V/Museum_of_Qatar.jpg',
  accentColor: 'from-orange-500 to-red-600'
}, {
  date: 'Feb 2',
  time: 'All Day',
  title: 'Web Summit Qatar',
  location: '📍 Doha Exhibition & Convention Centre (DECC)',
  description: 'Global innovation conference and networking',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/upys6wVWeWAXRdFjehHnGK/Screenshot_2026-01-12_at_3.47.01_PM.png',
  accentColor: 'from-cyan-500 to-blue-600'
}, {
  date: 'Feb 3',
  time: '3pm - 5:30pm',
  title: 'Demo Day at Scale 7',
  location: 'Presented by Qatar Development Bank and InDrive',
  description: 'Pitch your venture to investors and ecosystem partners',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/bk2XwH3ibxqDQt35zV2S6f/Pitch_Or_Party.jpg',
  accentColor: 'from-violet-500 to-purple-600'
}, {
  date: 'Feb 3',
  time: 'Evening',
  title: 'Doha Connect',
  location: 'Founder & Investor Mixer',
  description: "Ecosystem meetings and visits - connecting with investors, partners, and key stakeholders in Qatar's innovation landscape",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/kPe1kuaHWfnJv24G2RddZF/DOHAconnectevent.png',
  accentColor: 'from-pink-500 to-rose-600'
}];
const investors: Investor[] = [{
  name: 'Nada Shaheen',
  firm: 'GB Ventures',
  bio: 'Nada Shaheen is a prominent investment expert and the Managing Director of GB Ventures, the corporate venture capital (CVC) arm of GB Corp, a leading industrial group in the MENA region. As of 2025, she is recognized as the first Egyptian woman to lead and manage a CVC fund in Africa. She oversees strategic investments in high-growth sectors, including fintech, mobility, logistics, and industrial tech. Under her leadership, GB Ventures invests between $500,000 and $5 million in Egyptian and African startups to drive corporate innovation and sustainable growth.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9RxbGSNSLZbitMuyDXTJd3/Nada.jpg',
  accentColor: 'from-violet-500 to-purple-600'
}, {
  name: 'Alina Truhina',
  firm: 'A Typical Ventures / Utopia Capital',
  bio: 'Alina is the CEO and Managing Partner of The Radical Fund, an early-stage VC fund investing in founders who are rapidly scaling adaptation and mitigation solutions to build climate resilience in the region and deliver commercial returns. Alina is also the Co-Founder of Founders Factory Africa, the pan-African venture studio and VC Fund headquartered in Johannesburg and operating across East, West and South Africa. Alina has raised over US$140million from LPs and investors for the Africa funds, which currently has over 60 portfolio ventures, and over 50 team members across the continent.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/hmscgVojdgdzm1i4ir5Yza/Alina.png',
  accentColor: 'from-cyan-500 to-blue-600'
}, {
  name: 'William Bao Bean',
  firm: 'Orbit Ventures',
  bio: "William Bao Bean is Managing General Partner at Orbit Ventures and Venture Partner at SOSV, where he focuses on growth-stage investments in emerging markets. Since joining the VC ecosystem after senior roles at SingTel Innov8 Ventures and SoftBank China & India Holdings, he has backed standout companies such as Yodo1, GMobi and DemystData. Earlier in his career he spent over a decade as an equity research analyst at Deutsche Bank covering Asia's internet and telecom sectors. He holds a BA from Bowdoin College.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/jrz4vqh8j2zyJrJTUD68T4/william-bao-bean-profile.jpg',
  accentColor: 'from-pink-500 to-rose-600'
}, {
  name: 'Tarik Sultan',
  firm: 'Sultan Ventures',
  bio: 'Tarik Sultan is a prominent investor and entrepreneur known for his work in the Middle East and the United States. Current Roles (2025): He serves as a Partner and Head of MENA (Middle East and North Africa) for Builders VC, where he focuses on investment strategy and startup scaling. Entrepreneurship: He is the Managing Partner of Sultan Ventures and founder of XLR8UH, a venture accelerator at the University of Hawaii.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/tKkyVcFeqFzyseZjqGFnPZ/Tarik.jpg',
  accentColor: 'from-amber-500 to-orange-600'
}, {
  name: 'Michael Lints',
  firm: 'Golden Gate Ventures',
  bio: 'Michael leads growth ventures at Golden Gate Ventures, managing LP fundraising and portfolio expansion for Series B and later-stage investments. Since 2013, he has raised over USD $60M, driven strategic acquisitions, and supported scaling across Southeast Asia. Previously, he founded a venture fund for Dutch SMEs and co-founded an IT managed services startup acquired by a major European telecom company.',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/9G2zx1PCm8e2cDkaZVqhaZ/MichaelLints.webp',
  accentColor: 'from-indigo-500 to-blue-600'
}, {
  name: 'Soumaya Ben Beya Dridje',
  firm: 'Rasmal Ventures',
  bio: "Soumaya Ben Beya Dridje is a Partner at Rasmal Ventures, the fund recently held its first close with over $30 million in commitments from investors including the Qatar Investment Authority (QIA), family offices, high-net-worth individuals, corporate investors, and Tunisia's ANAVA fund of funds. Rasmal Ventures invests in high-growth technology startups such as Carthagea, InstaDeep, Expensya, and SeekMake, focusing on fintech, health tech, B2B SaaS, and artificial intelligence from pre-Series A to Series B stages.",
  imageUrl: 'https://cdn.magicpatterns.com/uploads/wicqnXthK73mKq33epWPHb/soumaya.png',
  accentColor: 'from-emerald-500 to-teal-600'
}];
const outcomes: Outcome[] = [{
  title: 'Investor Readiness',
  description: 'Fundraising clarity, confidence, and readiness for global capital',
  icon: <TrendingUpIcon className="w-6 h-6" data-id="element-2" />,
  gradient: 'from-cyan-500 to-blue-600',
  accentColor: 'from-cyan-400 to-blue-500'
}, {
  title: 'Market Expansion',
  description: 'Practical insight into operating and selling in MENA markets',
  icon: <GlobeIcon className="w-6 h-6" data-id="element-3" />,
  gradient: 'from-violet-500 to-purple-600',
  accentColor: 'from-violet-400 to-purple-500'
}, {
  title: 'Global Positioning',
  description: 'Founder visibility and credibility at international platforms',
  icon: <SparklesIcon className="w-6 h-6" data-id="element-4" />,
  gradient: 'from-pink-500 to-rose-600',
  accentColor: 'from-pink-400 to-rose-500'
}, {
  title: 'Cultural Excellence',
  description: 'Confident, respectful, and informed engagement in Qatar',
  icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-5" />,
  gradient: 'from-amber-500 to-orange-600',
  accentColor: 'from-amber-400 to-orange-500'
}];
function GeometricPattern() {
  return <svg className="absolute top-0 right-0 w-96 h-96 opacity-10" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6">
      {/* Hexagonal grid pattern inspired by Doha architecture */}
      <defs data-id="element-7">
        <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-8">
          <stop offset="0%" stopColor="#06b6d4" data-id="element-9" />
          <stop offset="100%" stopColor="#ec4899" data-id="element-10" />
        </linearGradient>
      </defs>
      {/* Large hexagon */}
      <polygon points="200,20 340,100 340,260 200,340 60,260 60,100" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-11" />
      {/* Medium hexagon */}
      <polygon points="200,60 300,120 300,240 200,300 100,240 100,120" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-12" />
      {/* Small hexagon */}
      <polygon points="200,100 260,140 260,220 200,260 140,220 140,140" stroke="url(#hexGradient)" strokeWidth="1" fill="none" data-id="element-13" />
      {/* Connecting lines */}
      <line x1="200" y1="20" x2="200" y2="100" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-14" />
      <line x1="340" y1="100" x2="260" y2="140" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-15" />
      <line x1="340" y1="260" x2="260" y2="220" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-16" />
      <line x1="200" y1="340" x2="200" y2="260" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-17" />
      <line x1="60" y1="260" x2="140" y2="220" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-18" />
      <line x1="60" y1="100" x2="140" y2="140" stroke="url(#hexGradient)" strokeWidth="1" data-id="element-19" />
      {/* Decorative dots */}
      <circle cx="200" cy="180" r="4" fill="url(#hexGradient)" data-id="element-20" />
      <circle cx="200" cy="20" r="3" fill="#06b6d4" data-id="element-21" />
      <circle cx="340" cy="100" r="3" fill="#ec4899" data-id="element-22" />
      <circle cx="340" cy="260" r="3" fill="#06b6d4" data-id="element-23" />
      <circle cx="200" cy="340" r="3" fill="#ec4899" data-id="element-24" />
      <circle cx="60" cy="260" r="3" fill="#06b6d4" data-id="element-25" />
      <circle cx="60" cy="100" r="3" fill="#ec4899" data-id="element-26" />
    </svg>;
}
function GeometricPatternLeft() {
  return <svg className="absolute bottom-0 left-0 w-64 h-64 opacity-5" viewBox="0 0 300 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-27">
      <defs data-id="element-28">
        <linearGradient id="hexGradient2" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-29">
          <stop offset="0%" stopColor="#ec4899" data-id="element-30" />
          <stop offset="100%" stopColor="#06b6d4" data-id="element-31" />
        </linearGradient>
      </defs>
      <polygon points="150,30 250,90 250,210 150,270 50,210 50,90" stroke="url(#hexGradient2)" strokeWidth="1.5" fill="none" data-id="element-32" />
      <polygon points="150,70 210,110 210,190 150,230 90,190 90,110" stroke="url(#hexGradient2)" strokeWidth="1" fill="none" data-id="element-33" />
    </svg>;
}
function TabNavigation({
  activeTab,
  setActiveTab
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  return <div className="flex justify-center mb-12" data-id="element-34">
      <div className="inline-flex bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-1.5 flex-wrap gap-1" data-id="element-35">
        <button onClick={() => setActiveTab('growth-labs')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'growth-labs' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-36">
          Growth Labs
        </button>
        <button onClick={() => setActiveTab('foundhers-delegation')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'foundhers-delegation' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-37">
          FoundHers+ Delegation
        </button>
        <button onClick={() => setActiveTab('doha-itinerary')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'doha-itinerary' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-38">
          Doha Itinerary
        </button>
        <button onClick={() => setActiveTab('qatar-ecosystem')} className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'qatar-ecosystem' ? 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white shadow-lg shadow-cyan-500/30' : 'text-slate-400 hover:text-white'}`} data-id="element-39">
          Qatar Ecosystem
        </button>
      </div>
    </div>;
}
function ActivityCard({
  activity,
  index
}: {
  activity: Activity;
  index: number;
}) {
  const isEven = index % 2 === 0;
  // Determine gradient based on activity type and specific facilitators
  const getGradient = () => {
    // Special colors for specific facilitators
    if (activity.deliveryLead === 'Chido Samantha Mbavarira') return 'from-pink-500 to-rose-600';
    if (activity.deliveryLead === 'Layla Freig') return 'from-pink-500 to-rose-600';
    // Default colors based on session type
    if (activity.isMandatory) return 'from-emerald-500 to-green-600';
    if (activity.sessionFormat === '1:1 Sessions') return 'from-violet-500 to-purple-600';
    if (activity.sessionFormat === 'Live Group Session') return 'from-cyan-500 to-blue-600';
    return 'from-amber-500 to-orange-600';
  };
  const isBryanDuarte = activity.deliveryLead === 'Bryan Duarte';
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 items-start`} data-id="element-40">
      {/* Facilitator Image */}
      <div className="flex-shrink-0 w-full md:w-48" data-id="element-41">
        <div className="relative" data-id="element-42">
          {/* Gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${getGradient()} rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-43"></div>

          {/* Image container */}
          <div className="relative rounded-2xl overflow-hidden h-48 bg-slate-800" data-id="element-44">
            {activity.imageUrl ? <img src={activity.imageUrl} alt={activity.deliveryLead} className={`w-full h-full ${activity.imageType === 'logo' ? 'object-contain p-6' : 'object-cover object-top'}`} data-id="element-45" /> : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800" data-id="element-46">
                <span className="text-4xl font-bold text-white" data-id="element-47">
                  {activity.initials}
                </span>
              </div>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1" data-id="element-48">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300 h-full" data-id="element-49">
          {/* Date and badges */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-50">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${getGradient()} bg-opacity-20 border border-white/20`} data-id="element-51">
              <CalendarIcon className="w-4 h-4 text-white" data-id="element-52" />
              <span className="text-sm font-semibold text-white" data-id="element-53">
                {activity.date}
              </span>
            </div>
            {activity.isMandatory && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30" data-id="element-54">
                <span className="text-xs font-bold text-emerald-300" data-id="element-55">
                  MANDATORY
                </span>
              </div>}
            {activity.sessionFormat && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-500/30" data-id="element-56">
                <span className="text-xs font-semibold text-cyan-300" data-id="element-57">
                  {activity.sessionFormat}
                </span>
              </div>}
          </div>

          {/* Session title */}
          <h3 className="text-xl font-bold text-white mb-2 [text-wrap:balance]" data-id="element-58">
            {activity.session}
          </h3>

          {/* Delivery lead */}
          <div className="flex items-center gap-2 mb-3" data-id="element-59">
            <UserIcon className="w-4 h-4 text-violet-400" data-id="element-60" />
            <span className="text-sm text-violet-300 font-medium" data-id="element-61">
              {activity.deliveryLead}
            </span>
          </div>

          {/* Focus description */}
          <p className="text-slate-300 leading-relaxed [text-wrap:pretty] mb-4" data-id="element-62">
            {activity.focus}
          </p>

          {/* Book Now button for Bryan Duarte */}
          {isBryanDuarte && <a href="https://calendly.com/bryanduarte/foundhers_45-minute-mvp_evaluation_with_bryan" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold hover:from-violet-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-violet-500/50" data-id="element-63">
              <CalendarIcon className="w-4 h-4" data-id="element-64" />
              <span data-id="element-65">Book Now</span>
            </a>}

          {/* Decorative accent line */}
          <div className={`mt-4 h-1 w-20 bg-gradient-to-r ${getGradient()} rounded-full`} data-id="element-66"></div>
        </div>
      </div>
    </div>;
}
function ItineraryCard({
  event,
  index
}: {
  event: ItineraryEvent;
  index: number;
}) {
  const isEven = index % 2 === 0;
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`} data-id="element-67">
      {/* Image with futuristic border */}
      <div className="flex-shrink-0 w-full md:w-1/2" data-id="element-68">
        <div className="relative" data-id="element-69">
          {/* Animated gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${event.accentColor} rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-70"></div>

          {/* Image container */}
          <div className="relative rounded-3xl overflow-hidden h-80" data-id="element-71">
            <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-72" />
            {/* Gradient overlay */}
            <div className={`absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60`} data-id="element-73"></div>

            {/* Date badge floating on image */}
            <div className="absolute bottom-6 right-6" data-id="element-74">
              <div className={`bg-gradient-to-r ${event.accentColor} rounded-2xl px-6 py-3 shadow-2xl`} data-id="element-75">
                <span className="text-2xl font-bold text-white" data-id="element-76">
                  {event.date}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full md:w-1/2" data-id="element-77">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-78">
          {/* Time badge */}
          {event.time && <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 mb-4" data-id="element-79">
              <ClockIcon className="w-4 h-4 text-cyan-300" data-id="element-80" />
              <span className="text-sm font-semibold text-cyan-300" data-id="element-81">
                {event.time}
              </span>
            </div>}

          {/* Title */}
          <h3 className="text-3xl font-bold text-white mb-4 leading-tight [text-wrap:balance]" data-id="element-82">
            {event.title}
          </h3>

          {/* Location */}
          {event.location && <div className="flex items-start gap-3 mb-4" data-id="element-83">
              <MapPinIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-84" />
              <span className="text-lg text-violet-300 font-medium [text-wrap:balance]" data-id="element-85">
                {event.location}
              </span>
            </div>}

          {/* Description */}
          <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-86">
            {event.description}
          </p>

          {/* Decorative accent line */}
          <div className={`mt-6 h-1 w-24 bg-gradient-to-r ${event.accentColor} rounded-full`} data-id="element-87"></div>
        </div>
      </div>
    </div>;
}
function CityTourCard() {
  return <div className="group relative" data-id="element-88">
      <div className="relative flex flex-col gap-8" data-id="element-89">
        {/* Content card */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-90">
          {/* Time badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 mb-4" data-id="element-91">
            <ClockIcon className="w-4 h-4 text-cyan-300" data-id="element-92" />
            <span className="text-sm font-semibold text-cyan-300" data-id="element-93">
              Morning & Afternoon
            </span>
          </div>

          {/* Date badge */}
          <div className="inline-flex items-center gap-3 mb-6" data-id="element-94">
            <div className="bg-gradient-to-r from-pink-500 to-rose-600 rounded-2xl px-6 py-3 shadow-2xl" data-id="element-95">
              <span className="text-2xl font-bold text-white" data-id="element-96">Feb 4</span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-3xl font-bold text-white mb-4 leading-tight" data-id="element-97">
            Meeting at Trade Commissioner's Office & City Tour
          </h3>

          {/* Location */}
          <div className="flex items-start gap-3 mb-4" data-id="element-98">
            <MapPinIcon className="w-5 h-5 text-violet-400 flex-shrink-0 mt-1" data-id="element-99" />
            <span className="text-lg text-violet-300 font-medium" data-id="element-100">
              Doha City
            </span>
          </div>

          {/* Description */}
          <p className="text-slate-300 leading-relaxed text-lg mb-8" data-id="element-101">
            Strategic meetings followed by cultural immersion and city
            exploration
          </p>

          {/* Image grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-102">
            {/* QDB Building */}
            <div className="relative" data-id="element-103">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-violet-400 to-cyan-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-104"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-105">
                <img src="https://cdn.magicpatterns.com/uploads/ip5kngqFtgMiH91zTFMdsH/qatar-development-bank-head-office5-1651747582.webp" alt="Qatar Development Bank" className="w-full h-full object-cover" data-id="element-106" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-107"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-108">
                  <p className="text-white text-sm font-semibold" data-id="element-109">
                    Qatar Development Bank
                  </p>
                </div>
              </div>
            </div>

            {/* Doha Skyline */}
            <div className="relative" data-id="element-110">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-violet-400 to-pink-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-111"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-112">
                <img src="https://cdn.magicpatterns.com/uploads/nEjauh7qKyAfaNhFCjRepb/temp_file_Qatar_-_Doha_-_NS1-700x467.jpg" alt="Doha Skyline" className="w-full h-full object-cover" data-id="element-113" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-114"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-115">
                  <p className="text-white text-sm font-semibold" data-id="element-116">
                    Doha Skyline
                  </p>
                </div>
              </div>
            </div>

            {/* Luxury Vehicle Tour */}
            <div className="relative" data-id="element-117">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-400 to-cyan-400 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-118"></div>
              <div className="relative rounded-2xl overflow-hidden h-48" data-id="element-119">
                <img src="https://cdn.magicpatterns.com/uploads/qA9vJ1Q5iM5CwXSofrqnTo/8_Best_SUVs_in_Qatar_Title_image_cover.jpg" alt="Luxury City Tour" className="w-full h-full object-cover" data-id="element-120" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-121"></div>
                <div className="absolute bottom-3 left-3 right-3" data-id="element-122">
                  <p className="text-white text-sm font-semibold" data-id="element-123">
                    Luxury City Tour
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative accent line */}
          <div className="mt-6 h-1 w-24 bg-gradient-to-r from-pink-500 to-rose-600 rounded-full" data-id="element-124"></div>
        </div>
      </div>
    </div>;
}
function InvestorCard({
  investor,
  index
}: {
  investor: Investor;
  index: number;
}) {
  const isEven = index % 2 === 0;
  return <div className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-start`} data-id="element-125">
      {/* Photo */}
      <div className="flex-shrink-0 w-full md:w-80" data-id="element-126">
        <div className="relative" data-id="element-127">
          {/* Gradient border */}
          <div className={`absolute -inset-1 bg-gradient-to-r ${investor.accentColor} rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-500`} data-id="element-128"></div>

          {/* Image container */}
          <div className="relative rounded-3xl overflow-hidden h-96 bg-slate-800" data-id="element-129">
            <img src={investor.imageUrl} alt={investor.name} className="w-full h-full object-cover object-top" data-id="element-130" />
          </div>
        </div>
      </div>

      {/* Bio content */}
      <div className="flex-1" data-id="element-131">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 hover:bg-white/8 hover:border-white/20 transition-all duration-300 h-full" data-id="element-132">
          {/* Name and firm */}
          <div className="mb-6" data-id="element-133">
            <h3 className="text-3xl font-bold text-white mb-2 [text-wrap:balance]" data-id="element-134">
              {investor.name}
            </h3>
            <div className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${investor.accentColor} bg-opacity-20 border border-white/20`} data-id="element-135">
              <span className="text-sm font-semibold text-white [text-wrap:balance]" data-id="element-136">
                {investor.firm}
              </span>
            </div>
          </div>

          {/* Bio */}
          <p className="text-slate-300 leading-relaxed text-base [text-wrap:pretty]" data-id="element-137">
            {investor.bio}
          </p>

          {/* Decorative accent line */}
          <div className={`mt-6 h-1 w-24 bg-gradient-to-r ${investor.accentColor} rounded-full`} data-id="element-138"></div>
        </div>
      </div>
    </div>;
}
function OutcomeCard({
  outcome
}: {
  outcome: Outcome;
}) {
  return <div className="group relative" data-id="element-139">
      {/* Animated gradient border */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${outcome.gradient} rounded-2xl blur opacity-60 group-hover:opacity-100 transition duration-500`} data-id="element-140"></div>

      <div className="relative bg-slate-900/90 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-slate-900/70 transition-all duration-300 h-full" data-id="element-141">
        {/* Icon with gradient background */}
        <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${outcome.gradient} flex items-center justify-center mb-4 shadow-lg`} data-id="element-142">
          {/* Glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-br ${outcome.gradient} rounded-xl blur-md opacity-50`} data-id="element-143"></div>
          <div className="relative text-white" data-id="element-144">{outcome.icon}</div>
        </div>

        {/* Title with gradient accent */}
        <h3 className="text-xl font-bold text-white mb-3 [text-wrap:balance]" data-id="element-145">
          {outcome.title}
        </h3>

        {/* Description */}
        <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-146">
          {outcome.description}
        </p>

        {/* Decorative accent line with gradient */}
        <div className={`mt-5 h-1 w-24 bg-gradient-to-r ${outcome.accentColor} rounded-full shadow-lg`} data-id="element-147"></div>

        {/* Decorative corner accent */}
        <div className={`absolute top-4 right-4 w-8 h-8 bg-gradient-to-br ${outcome.gradient} opacity-10 rounded-full blur-sm`} data-id="element-148"></div>
      </div>
    </div>;
}
function FoundHersDelegation() {
  return <div className="space-y-16" data-id="element-149">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-150">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/20 to-violet-500/20 border border-pink-500/30 mb-6" data-id="element-151">
          <SparklesIcon className="w-4 h-4 text-pink-300" data-id="element-152" />
          <span className="text-sm font-semibold text-pink-300" data-id="element-153">
            Women-Led Innovation
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-154">
          The FoundHers+ Delegation
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-155">
          A powerful cohort of women-led startups redefining innovation across
          global markets
        </p>
      </div>

      {/* Delegation Description */}
      <section data-id="element-156">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-157">
          <div className="flex items-center gap-3 mb-6" data-id="element-158">
            <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-159" />
            <h2 className="text-2xl font-bold text-white" data-id="element-160">
              About the Delegation
            </h2>
          </div>
          <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-161">
            <p className="[text-wrap:pretty]" data-id="element-162">
              The FoundHers delegation brings together a powerful cohort of
              women-led startups operating at the forefront of innovation across
              SaaS, FinTech, HealthTech, MedTech, Cleantech, HR Tech, and
              Defence Tech. These founders represent scalable, impact-driven
              businesses building solutions for global markets, with strong
              traction, ambitious growth plans, and a commitment to inclusive
              innovation.
            </p>
            <p className="[text-wrap:pretty]" data-id="element-163">
              Together, this delegation reflects the depth and diversity of the
              FoundHers ecosystem, companies solving real-world problems through
              technology, data, and design, while redefining leadership in their
              respective industries.
            </p>
          </div>
        </div>
      </section>

      {/* Industry Sectors */}
      <section data-id="element-164">
        <div className="flex items-center gap-3 mb-8" data-id="element-165">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-166" />
          <h2 className="text-2xl font-bold text-white" data-id="element-167">
            Industry Focus Areas
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-168">
          {/* SaaS */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-169">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-170">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-171" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-172">SaaS</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-173">
              Software-as-a-Service platforms driving digital transformation
            </p>
          </div>

          {/* FinTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-174">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4" data-id="element-175">
              <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-176" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-177">FinTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-178">
              Financial technology solutions revolutionizing banking and
              payments
            </p>
          </div>

          {/* HealthTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-179">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4" data-id="element-180">
              <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-181" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-182">HealthTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-183">
              Digital health solutions improving patient care and outcomes
            </p>
          </div>

          {/* MedTech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-184">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4" data-id="element-185">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-186" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-187">MedTech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-188">
              Medical technology innovations advancing healthcare delivery
            </p>
          </div>

          {/* Cleantech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-189">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4" data-id="element-190">
              <GlobeIcon className="w-6 h-6 text-white" data-id="element-191" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-192">Cleantech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-193">
              Sustainable technology solutions for climate and energy challenges
            </p>
          </div>

          {/* HR Tech */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-194">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-195">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-196" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-197">HR Tech</h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-198">
              Workforce technology transforming talent management and operations
            </p>
          </div>
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-199">
        <div className="bg-gradient-to-r from-pink-500/10 via-violet-500/10 to-cyan-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-200">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-201">
            Building the Future Together
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-202">
            This delegation represents the next generation of women-led
            innovation, combining technical excellence with market insight and a
            commitment to creating lasting impact across industries and
            geographies.
          </p>
        </div>
      </div>
    </div>;
}
function DohaItinerary() {
  return <div className="space-y-16" data-id="element-203">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-204">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-205">
          Your Doha Journey
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-206">
          Four days of innovation, culture, and strategic connections in the
          heart of Qatar
        </p>
      </div>

      {/* Accommodation Section */}
      <section data-id="element-207">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-208">
          <div className="flex items-center gap-3 mb-6" data-id="element-209">
            <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full" data-id="element-210" />
            <h2 className="text-2xl font-bold text-white" data-id="element-211">
              Accommodation & Logistics
            </h2>
          </div>

          <div className="space-y-6 mb-8" data-id="element-212">
            <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-213">
              <p className="[text-wrap:pretty]" data-id="element-214">
                All guests are expected to arrive in Doha by{' '}
                <span className="text-white font-semibold" data-id="element-215">February 1</span>,
                with check-out scheduled for{' '}
                <span className="text-white font-semibold" data-id="element-216">February 5</span>.
                Programming will take place throughout this period, including
                planned meetings, ecosystem engagements, and Demo Day
                activities.
              </p>
              <p className="[text-wrap:pretty]" data-id="element-217">
                Participants will be staying at{' '}
                <span className="text-cyan-300 font-semibold" data-id="element-218">W Doha</span>,
                selected for its central location, world-class hospitality, and
                premium amenities. The hotel offers a refined yet vibrant
                environment designed for both productivity and relaxation;
                ensuring guests enjoy a seamless, comfortable experience
                throughout their stay. From thoughtfully designed rooms to
                exceptional dining and wellness facilities, the W Doha provides
                a luxury setting that supports meaningful connection, rest, and
                focus during the delegation.
              </p>
            </div>
          </div>

          {/* Hotel Images Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-219">
            {/* Rooftop/Lounge Area */}
            <div className="relative group" data-id="element-220">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-blue-400 to-violet-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-221"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-222">
                <img src="https://cdn.magicpatterns.com/uploads/dz84zR4aEHbCtUBfApF4JQ/medium-1280px.jpg" alt="W Doha Rooftop Lounge" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-223" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-224"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-225">
                  <p className="text-white text-sm font-semibold" data-id="element-226">
                    Rooftop Lounge
                  </p>
                </div>
              </div>
            </div>

            {/* Suite Interior */}
            <div className="relative group" data-id="element-227">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-pink-400 to-orange-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-228"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-229">
                <img src="https://cdn.magicpatterns.com/uploads/cvSXo8ZLWoJDghANPTBKRu/02c-1-1024x682.jpg" alt="W Doha Suite" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-230" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-231"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-232">
                  <p className="text-white text-sm font-semibold" data-id="element-233">
                    Premium Suite
                  </p>
                </div>
              </div>
            </div>

            {/* Guest Room */}
            <div className="relative group" data-id="element-234">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-rose-400 to-cyan-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-500" data-id="element-235"></div>
              <div className="relative rounded-2xl overflow-hidden h-64" data-id="element-236">
                <img src="https://cdn.magicpatterns.com/uploads/4PBbBVHkFSBMqCWZPuQsH8/WDoha.jpg" alt="W Doha Guest Room" className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500" data-id="element-237" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" data-id="element-238"></div>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-239">
                  <p className="text-white text-sm font-semibold" data-id="element-240">Guest Room</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary events */}
      <section data-id="element-241">
        <div className="flex items-center gap-3 mb-12" data-id="element-242">
          <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-243" />
          <h2 className="text-2xl font-bold text-white" data-id="element-244">Daily Schedule</h2>
        </div>

        <div className="space-y-20" data-id="element-245">
          {itineraryEvents.map((event, index) => <ItineraryCard key={index} event={event} index={index} data-id="element-246" />)}

          {/* Special card for Feb 4 with multiple images */}
          <CityTourCard data-id="element-247" />
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-248">
        <div className="bg-gradient-to-r from-cyan-500/10 via-violet-500/10 to-pink-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-249">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-250">
            Ready for the Experience?
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-251">
            This carefully curated itinerary combines strategic business
            development with cultural immersion, designed to maximize your
            impact and connections in Qatar's innovation ecosystem.
          </p>
        </div>
      </div>
    </div>;
}
function QatarEcosystem() {
  return <div className="space-y-16" data-id="element-252">
      {/* Hero section */}
      <div className="text-center mb-12" data-id="element-253">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6" data-id="element-254">
          <GlobeIcon className="w-4 h-4 text-amber-300" data-id="element-255" />
          <span className="text-sm font-semibold text-amber-300" data-id="element-256">
            Strategic Partnerships
          </span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-257">
          Qatar Innovation Ecosystem
        </h2>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto [text-wrap:balance]" data-id="element-258">
          Connecting with global investors and strategic partners across MENA
          and beyond
        </p>
      </div>

      {/* Investor & Stakeholder Context */}
      <section data-id="element-259">
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-260">
          <div className="flex items-center gap-3 mb-6" data-id="element-261">
            <div className="w-1 h-8 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full" data-id="element-262" />
            <h2 className="text-2xl font-bold text-white" data-id="element-263">
              Investors & Strategic Partners
            </h2>
          </div>
          <div className="space-y-4 text-slate-300 leading-relaxed text-lg" data-id="element-264">
            <p className="[text-wrap:pretty]" data-id="element-265">
              The investors and stakeholders we have scheduled to meet are a
              highly curated group of global investors representing leading
              venture capital firms, corporate venture arms, and strategic
              investment platforms across North America, Europe, MENA, Africa,
              and Asia. Collectively, these investors bring deep experience in
              deploying institutional capital, scaling technology companies from
              early traction through growth stages, and supporting founders
              across complex, cross-border markets.
            </p>
            <p className="[text-wrap:pretty]" data-id="element-266">
              This investor group offers more than capital—they provide
              strategic insight, market access, and long-term partnership
              potential. With expertise spanning fintech, health and medtech,
              B2B SaaS, AI, climate and energy, workforce technologies, and
              industrial innovation, they are uniquely positioned to support
              women-led companies building category-defining solutions with
              global relevance. Together, they represent conviction in the
              FoundHers mission and the next generation of scalable, women-led
              innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Investor Profiles */}
      <section data-id="element-267">
        <div className="flex items-center gap-3 mb-12" data-id="element-268">
          <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-269" />
          <h2 className="text-2xl font-bold text-white" data-id="element-270">
            Demo Day Investors (Proposed)
          </h2>
        </div>

        <div className="space-y-16" data-id="element-271">
          {investors.map((investor, index) => <InvestorCard key={index} investor={investor} index={index} data-id="element-272" />)}
        </div>
      </section>

      {/* Key Ecosystem Pillars */}
      <section className="mt-20" data-id="element-273">
        <div className="flex items-center gap-3 mb-8" data-id="element-274">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-275" />
          <h2 className="text-2xl font-bold text-white" data-id="element-276">Ecosystem Pillars</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-277">
          {/* Venture Capital */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-278">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4" data-id="element-279">
              <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-280" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-281">
              Venture Capital
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-282">
              Leading VC firms with deep expertise in scaling technology
              companies across global markets
            </p>
          </div>

          {/* Corporate Venture */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-283">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4" data-id="element-284">
              <SparklesIcon className="w-6 h-6 text-white" data-id="element-285" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-286">
              Corporate Innovation
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-287">
              Strategic corporate partners offering market access and
              partnership opportunities
            </p>
          </div>

          {/* Government & Development */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-288">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4" data-id="element-289">
              <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-290" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-291">
              Government & Development
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-292">
              Public-private partnerships and development banks supporting
              regional growth
            </p>
          </div>

          {/* Global Networks */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300" data-id="element-293">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4" data-id="element-294">
              <GlobeIcon className="w-6 h-6 text-white" data-id="element-295" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3" data-id="element-296">
              Global Networks
            </h3>
            <p className="text-slate-300 leading-relaxed [text-wrap:pretty]" data-id="element-297">
              Cross-border connections spanning North America, Europe, MENA,
              Africa, and Asia
            </p>
          </div>
        </div>
      </section>

      {/* Closing section */}
      <div className="mt-16 text-center" data-id="element-298">
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-pink-500/10 backdrop-blur-sm border border-white/10 rounded-3xl p-10" data-id="element-299">
          <h3 className="text-2xl font-bold text-white mb-4 [text-wrap:balance]" data-id="element-300">
            Building Global Partnerships
          </h3>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto [text-wrap:pretty]" data-id="element-301">
            This ecosystem represents a unique convergence of capital,
            expertise, and strategic support designed to accelerate women-led
            innovation on a global scale.
          </p>
        </div>
      </div>
    </div>;
}
export function DohaDocument() {
  const [activeTab, setActiveTab] = useState('growth-labs');
  // Set document title for SEO
  useEffect(() => {
    document.title = 'FoundHers Doha 2026';
  }, []);
  return <main className="min-h-screen w-full relative overflow-hidden" data-id="element-302">
      {/* Background Image with Purple Fade */}
      <div className="fixed inset-0 z-0" data-id="element-303">
        <img src="https://cdn.magicpatterns.com/uploads/x21oDNBHunJvXPHouZZXyx/Screenshot_2026-01-12_at_3.49.08_PM.png" alt="Doha Skyline" className="w-full h-full object-cover" data-id="element-304" />
        {/* Purple gradient overlay that fades out the image */}
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/95 via-violet-950/90 to-slate-900/95" data-id="element-305"></div>
      </div>

      {/* Existing decorative elements */}
      <div className="relative z-10" data-id="element-306">
        <GeometricPattern data-id="element-307" />
        <GeometricPatternLeft data-id="element-308" />

        {/* Ambient glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-3xl" data-id="element-309" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl" data-id="element-310" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-16" data-id="element-311">
        {/* Header Section */}
        <header className="text-center mb-16" data-id="element-312">
          {/* Logo */}
          <div className="flex justify-center mb-8" data-id="element-313">
            <div className="w-32 h-32" data-id="element-314">
              <img src="https://cdn.magicpatterns.com/uploads/f4wwvtUhSALA8cYNSKZT5L/FOUNDHERS_logo_white.png" alt="FoundHers+ Logo" className="w-full h-full object-contain" data-id="element-315" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-id="element-316">
            FoundHers+ Growth Labs
          </h1>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-pink-500/20 border border-cyan-500/30 mb-6" data-id="element-317">
            <span className="text-cyan-300 font-semibold" data-id="element-318">
              Doha Strategic Programming
            </span>
          </div>

          {/* Subtitle - Fixed widow words */}
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed [text-wrap:balance]" data-id="element-319">
            Preparing Women Founders for Investment, Market Expansion & Global
            Engagement
          </p>
        </header>

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} data-id="element-320" />

        {/* Tab Content */}
        {activeTab === 'growth-labs' && <>
            {/* Program Context */}
            <section className="mb-16" data-id="element-321">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-10" data-id="element-322">
                <div className="flex items-center gap-3 mb-6" data-id="element-323">
                  <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-violet-500 rounded-full" data-id="element-324" />
                  <h2 className="text-2xl font-bold text-white" data-id="element-325">
                    Program Overview
                  </h2>
                </div>
                <p className="text-slate-300 leading-relaxed text-lg [text-wrap:pretty]" data-id="element-326">
                  FoundHers+ Growth Labs is an intensive global growth
                  initiative and power sprint accelerator/bootcamp designed over
                  a few weeks to get the founders in the mindset for
                  fundraising, international expansion, and high-impact
                  engagement across global innovation ecosystems. The Doha,
                  Qatar experience combines investor readiness, cultural
                  intelligence, and strategic market exposure aligned with the
                  Web Summit and MENA opportunity landscape.
                </p>
              </div>
            </section>

            {/* Activities Schedule */}
            <section className="mb-16" data-id="element-327">
              <div className="flex items-center gap-3 mb-8" data-id="element-328">
                <div className="w-1 h-8 bg-gradient-to-b from-pink-400 to-violet-500 rounded-full" data-id="element-329" />
                <h2 className="text-2xl font-bold text-white" data-id="element-330">
                  Upcoming Activities & Strategic Sessions
                </h2>
              </div>

              <div className="space-y-6" data-id="element-331">
                {activities.map((activity, index) => <ActivityCard key={index} activity={activity} index={index} data-id="element-332" />)}
              </div>
            </section>

            {/* Strategic Outcomes */}
            <section className="mb-16" data-id="element-333">
              <div className="flex items-center gap-3 mb-8" data-id="element-334">
                <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-pink-500 rounded-full" data-id="element-335" />
                <h2 className="text-2xl font-bold text-white" data-id="element-336">
                  Strategic Outcomes
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-337">
                {outcomes.map((outcome, index) => <OutcomeCard key={index} outcome={outcome} data-id="element-338" />)}
              </div>
            </section>
          </>}

        {activeTab === 'foundhers-delegation' && <FoundHersDelegation data-id="element-339" />}

        {activeTab === 'doha-itinerary' && <DohaItinerary data-id="element-340" />}

        {activeTab === 'qatar-ecosystem' && <QatarEcosystem data-id="element-341" />}

        {/* Footer */}
        <footer className="border-t border-white/10 pt-10 mt-16" data-id="element-342">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-343">
            {/* Logo and tagline */}
            <div className="flex items-center gap-4" data-id="element-344">
              <div className="w-16 h-16" data-id="element-345">
                <img src="https://cdn.magicpatterns.com/uploads/f4wwvtUhSALA8cYNSKZT5L/FOUNDHERS_logo_white.png" alt="FoundHers+ Logo" className="w-full h-full object-contain" data-id="element-346" />
              </div>
              <div data-id="element-347">
                <p className="text-white font-semibold" data-id="element-348">FoundHers+</p>
                <p className="text-sm text-slate-400 [text-wrap:balance]" data-id="element-349">
                  Building globally competitive women-led ventures
                </p>
              </div>
            </div>

            {/* Location and date */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10" data-id="element-350">
              <GlobeIcon className="w-4 h-4 text-cyan-400" data-id="element-351" />
              <span className="text-sm text-slate-300" data-id="element-352">
                Doha, Qatar | January 2026
              </span>
            </div>
          </div>
        </footer>
      </div>
    </main>;
}